import type {
  OgsmRemovalBlocker,
  OgsmRemovalCounts,
  OgsmRemovalImpact,
  OgsmRemovalOutcome,
} from '@/types/api'

/** A DELETE refused because the subtree holds data; its `details` is the removal impact. */
export const OGSM_NODE_HAS_DATA = 'OGSM_NODE_HAS_DATA'

export const OGSM_REMOVAL_BLOCKERS = [
  'OGSM_SESSION_PUBLISHED',
  'OGSM_SESSION_PREVIOUS_YEAR',
  'OGSM_NODE_KPI_ACTIVE',
] as const satisfies readonly OgsmRemovalBlocker[]

const OUTCOMES = [
  'DELETE',
  'ARCHIVE',
  'BLOCKED',
] as const satisfies readonly OgsmRemovalOutcome[]

export function isOgsmRemovalBlocker(
  code: unknown
): code is OgsmRemovalBlocker {
  return OGSM_REMOVAL_BLOCKERS.some((blocker) => blocker === code)
}

function isRemovalCounts(value: unknown): value is OgsmRemovalCounts {
  return (
    typeof value === 'object' &&
    value !== null &&
    'targetRevisions' in value &&
    typeof value.targetRevisions === 'number' &&
    'readings' in value &&
    typeof value.readings === 'number' &&
    'scorecardKpis' in value &&
    typeof value.scorecardKpis === 'number' &&
    'metricActuals' in value &&
    typeof value.metricActuals === 'number'
  )
}

/** Full shape check (nodeId, outcome, blockedBy, nodeCount, descendantCount, the four counts, metricActualsShared). */
export function isOgsmRemovalImpact(
  value: unknown
): value is OgsmRemovalImpact {
  return (
    typeof value === 'object' &&
    value !== null &&
    'nodeId' in value &&
    typeof value.nodeId === 'string' &&
    'outcome' in value &&
    OUTCOMES.some((outcome) => outcome === value.outcome) &&
    'blockedBy' in value &&
    (value.blockedBy === null || isOgsmRemovalBlocker(value.blockedBy)) &&
    'nodeCount' in value &&
    typeof value.nodeCount === 'number' &&
    'descendantCount' in value &&
    typeof value.descendantCount === 'number' &&
    'counts' in value &&
    isRemovalCounts(value.counts) &&
    'metricActualsShared' in value &&
    typeof value.metricActualsShared === 'boolean'
  )
}
